import React from 'react'
import {useStatesGlobal} from '../../../../_gori/helpers/components/StatesGlobalProvider'
import {useIntl} from 'react-intl'
import {SYNC_STATUS} from '../core/_const'
import {KTSVG, truncateText} from '../../../../_gori/helpers'
import clsx from 'clsx'

export const SyncAllStoresModal: React.FC = () => {
  const intl = useIntl()
  const {syncState, setSyncState, cancelSync, retrySync} = useStatesGlobal()

  const allStores = syncState.jobs?.flatMap((j) => j.stores) ?? []
  const failedStores = allStores.filter((s) => s.status === SYNC_STATUS.FAILED)
  const succeededStores = allStores.filter((s) => s.status === SYNC_STATUS.SUCCEEDED)

  const totalStores = allStores.length
  const failedStoresCount = failedStores.length
  const succeededStoresCount = succeededStores.length
  const pendingStoresCount = totalStores - succeededStoresCount
  const hasFailed = failedStores.length > 0

  const handleCancelSync = () => {
    cancelSync()
  }

  const handleRetrySync = () => {
    retrySync()
  }

  const handleClose = () => {
    setSyncState({type: SYNC_STATUS.IDLE, jobs: [], shouldReload: false})
  }

  return (
    <>
      {syncState.type !== SYNC_STATUS.IDLE &&
        syncState.type !== SYNC_STATUS.CANCELLED &&
        syncState.jobs?.length && (
          <div
            id='gori_popup_sync_on_all_stores'
            className='position-fixed rounded-2 border border-gray-300 p-4'
          >
            <div className='d-flex align-items-center justify-content-between mb-3'>
              <div className='d-flex align-items-center gap-2'>
                <div className='d-flex align-items-center justify-content-center'>
                  <KTSVG
                    className='align-items-center justify-content-center'
                    path='/media/gori/orders/shopping-bag.svg'
                  />
                  <span className='text-gray-700 fw-bold align-items-center justify-content-center ms-1'>
                    {intl.formatMessage({id: 'SYNCING_ALL_STORES'})}
                  </span>
                </div>
              </div>
            </div>

            {syncState.type === SYNC_STATUS.SYNCING && (
              <>
                <div className='small mb-1 text-muted'>
                  {intl.formatMessage(
                    {id: 'MESSAGE_STORES_SUCCESSFULLY_SYNCED'},
                    {succeededStores: succeededStoresCount, totalStores: totalStores}
                  )}
                </div>
                <div className='progress'>
                  <div
                    className='progress-bar'
                    style={{
                      width: `${(succeededStoresCount / totalStores) * 100 || 0}%`,
                    }}
                  ></div>
                </div>
                <div className='d-flex fw-bolder text-teal mt-5 mb-1 align-items-center'>
                  <span
                    className='btn-cancel cursor-pointer text-gray-700'
                    onClick={handleCancelSync}
                  >
                    {intl.formatMessage({id: 'CANCEL'})}
                  </span>
                </div>
              </>
            )}

            {syncState.type === SYNC_STATUS.RETRYING && (
              <>
                <div className='small mb-1 text-muted'>
                  {intl.formatMessage(
                    {id: 'MESSAGE_PREVIOUSLY_FAILED_STORES_SYNCED'},
                    {
                      succeededStores: syncState.retryDone ? succeededStoresCount : 0,
                      failedStores: pendingStoresCount,
                    }
                  )}
                </div>
                <div className='progress'>
                  <div
                    className='progress-bar'
                    style={{
                      width: `${
                        ((syncState.retryDone ? succeededStoresCount : 0) / failedStores.length) *
                          100 || 0
                      }%`,
                    }}
                  ></div>
                </div>
                <div className='d-flex fw-bolder text-teal mt-5 mb-1 align-items-center'>
                  <span
                    className='btn-cancel cursor-pointer text-gray-700'
                    onClick={handleCancelSync}
                  >
                    {intl.formatMessage({id: 'CANCEL'})}
                  </span>
                </div>
              </>
            )}

            {(syncState.type === SYNC_STATUS.SUCCEEDED ||
              syncState.type === SYNC_STATUS.FAILED) && (
              <>
                <div
                  className={clsx(
                    'd-flex fw-bold mt-3 align-items-center small',
                    hasFailed ? 'text-danger' : 'text-muted'
                  )}
                >
                  {hasFailed && (
                    <>
                      <KTSVG className='svg-icon me-1' path='/media/gori/orders/warning.svg' />
                      {failedStoresCount === 1
                        ? intl.formatMessage(
                            {id: 'MESSAGE_COUNT_STORE_FAILED_TO_SYNC'},
                            {failedStore: failedStoresCount}
                          )
                        : intl.formatMessage(
                            {id: 'MESSAGE_COUNT_STORES_FAILED_TO_SYNC'},
                            {failedStores: failedStoresCount}
                          )}
                    </>
                  )}
                  {!hasFailed && (
                    <>
                      {succeededStoresCount === 1
                        ? intl.formatMessage(
                            {id: 'MESSAGE_STORES_SUCCESSFULLY_SYNCED'},
                            {succeededStores: succeededStoresCount, totalStores: totalStores}
                          )
                        : intl.formatMessage(
                            {id: 'MESSAGE_STORES_SUCCESSFULLY_SYNCED'},
                            {succeededStores: succeededStoresCount, totalStores: totalStores}
                          )}
                    </>
                  )}
                </div>
                <div>
                  <div className='mt-2 small'>
                    {hasFailed && failedStores.length > 0 && (
                      <>
                        {failedStores.map((store) => (
                          <div
                            key={store.id}
                            className='fw-normal bg-white p-2 my-1 rounded-1 d-flex justify-content-between align-items-center'
                          >
                            <span>{truncateText(store.payload.store_name)}</span>
                            <KTSVG
                              className='svg-icon-2'
                              path='/media/gori/orders/sync-all-failed.svg'
                            />
                          </div>
                        ))}
                      </>
                    )}
                  </div>
                </div>

                {hasFailed && (
                  <div className='d-flex justify-content-between mt-5 mb-1 fw-bolder'>
                    <span
                      className='btn-retry cursor-pointer d-flex align-items-center'
                      onClick={handleRetrySync}
                    >
                      <KTSVG className='svg-icon-2' path='/media/gori/orders/sync-all.svg ' />
                      {intl.formatMessage({id: 'RETRY_SYNC'})}
                    </span>
                    <span
                      className='cursor-pointer d-flex align-items-center text-gray-700'
                      onClick={handleCancelSync}
                    >
                      {intl.formatMessage({id: 'CANCEL'})}
                    </span>
                  </div>
                )}
                {!hasFailed && (
                  <>
                    <div className='progress'>
                      <div
                        className='progress-bar'
                        style={{
                          width: `${(succeededStoresCount / totalStores) * 100 || 0}%`,
                        }}
                      ></div>
                    </div>
                    <div className='d-flex fw-bolder mt-5 mb-1 align-items-center'>
                      <span className='btn-retry cursor-pointer' onClick={handleClose}>
                        {intl.formatMessage({id: 'CLOSE'})}
                      </span>
                    </div>
                  </>
                )}
              </>
            )}
          </div>
        )}
    </>
  )
}
